import { useState } from "react";
import { Plus, X } from "lucide-react";
import {
  SURCHARGE_LABEL,
  SURCHARGE_TYPES,
  type SurchargeHandling,
  type SurchargeType,
} from "@clevis/shared-types";
import { Button, Input, Label, Select, cn } from "@clevis/shared-ui";

// One form for every way an expense comes in: reviewing a scanned supplier
// invoice or receipt, typing one in, or fixing an existing one. Money is
// typed in dollars and kept in cents; a line's total starts as qty × cost
// but follows the supplier's own figure once typed (that's what posts to
// QuickBooks in "expenses" mode).

export interface DraftLine {
  id?: string;
  description: string;
  quantity: number;
  unitCostCents: number;
  extendedCents: number;
  sku: string;
  jobId: string | null;
  orderedItemId: string | null;
  itemId: string | null;
}

export interface DraftSurcharge {
  id?: string;
  type: SurchargeType;
  label: string;
  amountCents: number;
  handling?: SurchargeHandling;
}

export interface ExpenseDraft {
  vendorName: string;
  invoiceNumber: string;
  invoiceDate: string | null;
  jobId: string | null;
  lines: DraftLine[];
  surcharges: DraftSurcharge[];
  note: string;
}

export interface JobOption {
  id: string;
  title: string;
}

export const emptyLine = (jobId: string | null = null): DraftLine => ({
  description: "",
  quantity: 1,
  unitCostCents: 0,
  extendedCents: 0,
  sku: "",
  jobId,
  orderedItemId: null,
  itemId: null,
});

const toDollars = (c: number) => (c / 100).toFixed(2);
const toCents = (s: string) => {
  const n = Number.parseFloat(s.replace(/[$,\s]/g, ""));
  return Number.isFinite(n) ? Math.round(Number((n * 100).toFixed(6))) : 0;
};
export const money = (c: number) =>
  (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

const HANDLING_LABEL: Record<SurchargeHandling, string> = {
  expense: "Own line",
  distribute: "Spread into parts",
  ignore: "Don't post",
};

function MoneyInput({ cents, onCents, className }: { cents: number; onCents: (c: number) => void; className?: string }) {
  const [text, setText] = useState(toDollars(cents));
  const [focused, setFocused] = useState(false);
  return (
    <Input
      inputMode="decimal"
      className={cn("text-right", className)}
      value={focused ? text : toDollars(cents)}
      onFocus={() => {
        setText(toDollars(cents));
        setFocused(true);
      }}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        setText(e.target.value);
        onCents(toCents(e.target.value));
      }}
    />
  );
}

export function ExpenseEditor({
  initial,
  jobs,
  lockJobId,
  showHandling,
  busy,
  saveLabel = "Save expense",
  onSave,
  onCancel,
}: {
  initial: ExpenseDraft;
  /** Jobs a line can be charged to (office). */
  jobs?: JobOption[];
  /** A tech's receipt: every line belongs to this job. */
  lockJobId?: string;
  /** Office: choose how each surcharge posts to QuickBooks. */
  showHandling?: boolean;
  busy?: boolean;
  saveLabel?: string;
  onSave: (draft: ExpenseDraft) => void;
  onCancel?: () => void;
}) {
  const [d, setD] = useState<ExpenseDraft>(initial);
  const set = (patch: Partial<ExpenseDraft>) => setD((x) => ({ ...x, ...patch }));
  const setLine = (i: number, patch: Partial<DraftLine>) =>
    setD((x) => ({ ...x, lines: x.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const setCharge = (i: number, patch: Partial<DraftSurcharge>) =>
    setD((x) => ({ ...x, surcharges: x.surcharges.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));

  const linesCents = d.lines.reduce((s, l) => s + l.extendedCents, 0);
  const chargesCents = d.surcharges.reduce((s, x) => s + x.amountCents, 0);
  const valid = d.vendorName.trim() && (d.lines.some((l) => l.description.trim()) || d.surcharges.length > 0);
  const multiJob = !lockJobId && (jobs?.length ?? 0) > 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div>
          <Label>Vendor</Label>
          <Input value={d.vendorName} onChange={(e) => set({ vendorName: e.target.value })} placeholder="Supplier or store" />
        </div>
        <div>
          <Label>Invoice / receipt #</Label>
          <Input value={d.invoiceNumber} onChange={(e) => set({ invoiceNumber: e.target.value })} />
        </div>
        <div>
          <Label>Date</Label>
          <Input type="date" value={d.invoiceDate ?? ""} onChange={(e) => set({ invoiceDate: e.target.value || null })} />
        </div>
      </div>
      {multiJob ? (
        <div>
          <Label>Job</Label>
          <Select value={d.jobId ?? ""} onChange={(e) => set({ jobId: e.target.value || null })}>
            <option value="">No job (overhead)</option>
            {jobs!.map((j) => (
              <option key={j.id} value={j.id}>
                {j.title}
              </option>
            ))}
          </Select>
        </div>
      ) : null}

      <div>
        <div className="mb-1 grid grid-cols-[1fr_4rem_6rem_6rem_1.5rem] gap-1.5 text-[11px] font-medium text-steel">
          <span>Item</span>
          <span className="text-right">Qty</span>
          <span className="text-right">Unit cost</span>
          <span className="text-right">Line total</span>
          <span />
        </div>
        {d.lines.map((l, i) => (
          <div key={i} className="mb-1.5">
            <div className="grid grid-cols-[1fr_4rem_6rem_6rem_1.5rem] items-center gap-1.5">
              <Input
                className="py-1.5"
                value={l.description}
                placeholder="What was bought"
                onChange={(e) => setLine(i, { description: e.target.value })}
              />
              <Input
                className="py-1.5 text-right"
                inputMode="decimal"
                value={String(l.quantity)}
                onChange={(e) => {
                  const q = Number.parseFloat(e.target.value) || 0;
                  setLine(i, { quantity: q, extendedCents: Math.round(q * l.unitCostCents) });
                }}
              />
              <MoneyInput
                className="py-1.5"
                cents={l.unitCostCents}
                onCents={(c) => setLine(i, { unitCostCents: c, extendedCents: Math.round(l.quantity * c) })}
              />
              <MoneyInput className="py-1.5" cents={l.extendedCents} onCents={(c) => setLine(i, { extendedCents: c })} />
              <button
                type="button"
                aria-label="Remove line"
                className="text-steel hover:text-danger"
                onClick={() => set({ lines: d.lines.filter((_, j) => j !== i) })}
              >
                <X size={14} />
              </button>
            </div>
            {multiJob && d.lines.length > 1 ? (
              <Select
                className="mt-1 py-1 text-xs"
                value={l.jobId ?? ""}
                onChange={(e) => setLine(i, { jobId: e.target.value || null })}
              >
                <option value="">{d.jobId ? "Same job as the expense" : "No job"}</option>
                {jobs!.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.title}
                  </option>
                ))}
              </Select>
            ) : null}
          </div>
        ))}
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-brandorange-dark hover:text-brandorange"
          onClick={() => set({ lines: [...d.lines, emptyLine(lockJobId ?? null)] })}
        >
          <Plus size={13} /> Add line
        </button>
      </div>

      <div>
        <p className="mb-1 text-[11px] font-medium text-steel">Freight, fuel, tax and other charges</p>
        {d.surcharges.map((s, i) => (
          <div key={i} className="mb-1.5 grid grid-cols-[8.5rem_1fr_6rem_1.5rem] items-center gap-1.5">
            <Select className="py-1.5 text-xs" value={s.type} onChange={(e) => setCharge(i, { type: e.target.value as SurchargeType })}>
              {SURCHARGE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {SURCHARGE_LABEL[t]}
                </option>
              ))}
            </Select>
            <div className="flex items-center gap-1.5">
              <Input className="py-1.5" value={s.label} onChange={(e) => setCharge(i, { label: e.target.value })} />
              {showHandling ? (
                <Select
                  className="w-36 shrink-0 py-1.5 text-xs"
                  value={s.handling ?? ""}
                  onChange={(e) => setCharge(i, { handling: (e.target.value || undefined) as SurchargeHandling | undefined })}
                >
                  <option value="">Company default</option>
                  {(Object.keys(HANDLING_LABEL) as SurchargeHandling[]).map((h) => (
                    <option key={h} value={h}>
                      {HANDLING_LABEL[h]}
                    </option>
                  ))}
                </Select>
              ) : null}
            </div>
            <MoneyInput className="py-1.5" cents={s.amountCents} onCents={(c) => setCharge(i, { amountCents: c })} />
            <button
              type="button"
              aria-label="Remove charge"
              className="text-steel hover:text-danger"
              onClick={() => set({ surcharges: d.surcharges.filter((_, j) => j !== i) })}
            >
              <X size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-brandorange-dark hover:text-brandorange"
          onClick={() => set({ surcharges: [...d.surcharges, { type: "shipping", label: "Freight", amountCents: 0 }] })}
        >
          <Plus size={13} /> Add charge
        </button>
      </div>

      <div>
        <Label>Note</Label>
        <Input value={d.note} onChange={(e) => set({ note: e.target.value })} placeholder="Optional" />
      </div>

      <div className="flex items-center justify-between border-t border-steel/15 pt-2">
        <p className="text-sm text-ink">
          Total <strong>{money(linesCents + chargesCents)}</strong>
          {chargesCents !== 0 ? (
            <span className="ml-1 text-xs text-steel">
              ({money(linesCents)} parts + {money(chargesCents)} charges)
            </span>
          ) : null}
        </p>
        <div className="flex items-center gap-2">
          {onCancel ? (
            <button type="button" className="text-xs text-steel underline hover:text-ink" onClick={onCancel}>
              Cancel
            </button>
          ) : null}
          <Button
            busy={busy}
            disabled={!valid}
            onClick={() =>
              onSave({
                ...d,
                lines: d.lines.filter((l) => l.description.trim()).map((l) => (lockJobId ? { ...l, jobId: lockJobId } : l)),
              })
            }
          >
            {saveLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
